import * as React from 'react';

import {
  ModalFrame,
  ModalFrameFooter,
  ModalFrameInner,
  ModalFrameMessage
} from 'components/modal';
import { captureException } from '@sentry/browser';

import { __t, applyTranslations } from 'utils/language';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import { NAME_MAX_LENGTH, isRequired, maxLength } from 'utils/validators';
import { API, destroy, patch } from 'utils/api';

import './accept-team-invite-modal.scss';

applyTranslations(require('./accept-team-invite-modal.i18n.json'));

interface Props {
  profileName: string,
  orgId: number,
  isNewAccount: boolean,
  userId: number
}

const AcceptTeamInviteModal: React.FC<Props> = ({
  profileName = '',
  orgId,
  isNewAccount,
  userId
}: Props) => {
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const getTitle = () => {
    const message = isNewAccount ? __t('accept_team_invite_modal.title') : __t('accept_team_invite_modal.title_with_team_name', { profileName });
    return (
      <div className="accept-team-invite-modal__header">
        {message}
      </div>
    );
  };

  const updateAndJoinTheTeam = async () => {
    const acceptInviteUrl = API.END_POINTS.API.acceptInvitation(orgId);

    setLoading(true);
    try {
      if (isNewAccount) {
        const updateUserUrl = API.END_POINTS.USERS.updateUser(userId);

        await patch(updateUserUrl, {
          data: {
            firstName,
            lastName
          }
        });
      }

      await patch(acceptInviteUrl);
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
      location.reload();
    }
  };

  const declineInvitation = async () => {
    const declineInviteUrl = API.END_POINTS.API.declineInvitation(orgId);

    setLoading(true);
    try {
      await destroy(declineInviteUrl);
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
      location.reload();
    }
  };

  const getFormFields = () => (
    <React.Fragment>
      <div className="accept-team-invite-modal__subheader">
        {__t('accept_team_invite_modal.subtitle')}
      </div>
      <FormField
        label={__t('user_account.signup_first_name')}
        fontSize="small"
        direction="column"
        labelClassName="accept-team-invite-modal__label"
        showRequiredMark
        validators={[
          isRequired(firstName),
          maxLength(firstName, NAME_MAX_LENGTH)
        ]}
      >
        <Input
          name="firstName"
          width="wide"
          value={firstName}
          onChange={(event) => setFirstName(event?.target?.value || '')}
        />
      </FormField>

      <FormField
        label={__t('user_account.signup_last_name')}
        direction="column"
        fontSize="small"
        labelClassName="accept-team-invite-modal__label"
        showRequiredMark
        validators={[
          isRequired(lastName),
          maxLength(lastName, NAME_MAX_LENGTH)
        ]}
      >
        <Input
          name="lastName"
          width="wide"
          value={lastName}
          onChange={(event) => setLastName(event?.target?.value || '')}
        />
      </FormField>
    </React.Fragment>
  );

  return (
    <div className="accept-team-invite-modal">
      <ModalFrame>
        <ModalFrameMessage
          message={getTitle()}
          className="accept-team-invite-modal__message"
        />
        <ModalFrameInner
          className="accept-team-invite-modal__inner"
        >
          <Form
            onSubmit={async () => {
              await updateAndJoinTheTeam();
            }}
          >
            {isNewAccount && getFormFields()}
            <div className="accept-team-invite-modal__info">
              <div className="accept-team-invite-modal__info--title">
                {__t('accept_team_invite_modal.info.title')}
              </div>
              <div className="accept-team-invite-modal__info--subtitle">
                {__t('accept_team_invite_modal.info.subtitle')}
              </div>
            </div>
            {
              !isNewAccount && (
                <div className="accept-team-invite-modal__disclaimer">
                  {__t('accept_team_invite_modal.disclaimer')}
                </div>
              )
            }
            <ModalFrameFooter className="accept-team-invite-modal__footer">
              <ActionButton
                color="destroy"
                disabledWithSpinner={loading}
                disabled={loading}
                onClick={declineInvitation}
              >
                {__t('accept_team_invite_modal.decline_invite_btn')}
              </ActionButton>
              <ActionButton
                type="submit"
                color="primary"
                disabledWithSpinner={loading}
                disabled={loading}
              >
                {isNewAccount ? __t('accept_team_invite_modal.join_collabary') : __t('accept_team_invite_modal.join_team_btn')}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    </div>
  );
};

export default AcceptTeamInviteModal;
